import { useState } from "react";
import { Bot, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, formatApiError } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { useCompany } from "../../components/layout/CompanyLayout";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import { StatusBanner, Summary, Observations } from "../../components/insights/InsightParts";
import { InsightResult, RunButton } from "../../components/insights/AiResult";

const Filters = ({ value, onChange, agencies, services }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value, ...(k === "agency_id" ? { service_id: "" } : {}) });
  return (
    <div className="card-surface mb-6 grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="ins-filters">
      <label className="text-xs text-slate-500">Du<Input type="date" value={value.date_from} onChange={set("date_from")} data-testid="ins-date-from" /></label>
      <label className="text-xs text-slate-500">Au<Input type="date" value={value.date_to} onChange={set("date_to")} data-testid="ins-date-to" /></label>
      <select className="field-select self-end" value={value.agency_id} onChange={set("agency_id")} data-testid="ins-agency">
        <option value="">Toutes agences</option>{(agencies || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <select className="field-select self-end" value={value.service_id} onChange={set("service_id")} data-testid="ins-service">
        <option value="">Tous services</option>{(services || []).filter((s) => !value.agency_id || s.agency_id === value.agency_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </div>
  );
};

const ClassifyButton = ({ pending, status, onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/company/insights/classify"); toast.success(`${data.queued} avis en cours de classification, actualisez dans une minute`); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!pending) {
    return null;
  }
  return <Button variant="outline" className="rounded-full" onClick={run} disabled={busy || !status?.available} data-testid="ai-classify-pending">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-1.5 h-4 w-4" />}Classer {Math.min(pending, 15)} avis en attente</Button>;
};

const History = ({ items, onOpen, current }) => (
  <div className="card-surface p-5" data-testid="ai-history">
    <p className="mb-3 font-display font-semibold">Analyses précédentes</p>
    {!items?.length && <p className="text-sm text-slate-500">Aucune analyse IA enregistrée.</p>}
    <ul className="divide-y divide-slate-100 text-sm">{(items || []).map((i) => (
      <li key={i.id}><button type="button" onClick={() => onOpen(i.id)} className={`w-full py-2 text-left hover:text-blue-700 ${current === i.id ? "font-semibold" : ""}`} data-testid={`ai-history-${i.id}`}>
        {i.kind === "monthly" ? "Résumé mensuel" : "Analyse"} · {new Date(i.period.from).toLocaleDateString("fr-FR")} – {new Date(i.period.to).toLocaleDateString("fr-FR")}
        <span className="block text-xs text-slate-500">{i.stats.sent} commentaires · {new Date(i.created_at).toLocaleString("fr-FR")}{i.created_by ? ` · ${i.created_by.name}` : " · automatique"}</span></button></li>
    ))}</ul>
  </div>
);

export default function Insights() {
  const { company, can } = useCompany();
  const [scope, setScope] = useState({ date_from: "", date_to: "", agency_id: "", service_id: "" });
  const [insight, setInsight] = useState(null);
  const agencies = useApi("/company/agencies");
  const services = useApi("/company/services");
  const status = useApi("/company/insights/status");
  const history = useApi("/company/insights");
  const qs = new URLSearchParams(Object.entries(scope).filter(([, v]) => v)).toString();
  const { data, loading, error, reload } = useApi(`/company/insights/overview?${qs}`);
  const open = async (id) => {
    try { const { data: d } = await api.get(`/company/insights/${id}`); setInsight(d); } catch (e) { toast.error(formatApiError(e)); }
  };
  const done = (d) => { setInsight(d); history.reload(); status.reload(); };
  return (
    <div>
      <PageHeader eyebrow={company.name} title="Analyse intelligente" description="Thèmes, tendances, signaux et recommandations à partir des feedbacks réellement enregistrés. Les résultats de l'IA sont des aides à la décision."
        actions={<div className="flex flex-wrap gap-2">{can("feedback:update") && <ClassifyButton pending={data?.pending_classification} status={status.data} onDone={reload} />}<RunButton status={status.data} scope={scope} onDone={done} /></div>} />
      <StatusBanner status={status.data} />
      <Filters value={scope} onChange={setScope} agencies={agencies.data} services={services.data} />
      {loading && !data && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && <div className={`space-y-8 ${loading ? "opacity-60" : ""}`}>
        <Summary o={data} />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2" data-testid="ai-panel">
            {insight ? <InsightResult ins={insight} onChange={setInsight} /> : (
              <div className="card-surface flex flex-col items-start gap-2 p-6 text-sm text-slate-600" data-testid="ai-empty">
                <Bot className="h-6 w-6 text-slate-400" /><p className="font-semibold text-slate-800">Synthèse IA</p>
                <p>Lancez une synthèse pour obtenir les motifs d'insatisfaction, problèmes récurrents, points forts et recommandations de la période filtrée, ou ouvrez une analyse précédente.</p>
                <p className="text-xs text-slate-500">Seuls les textes des avis non confidentiels sont transmis, après masquage des emails, téléphones, liens et noms de personnes.</p>
              </div>
            )}
          </div>
          <History items={history.data} onOpen={open} current={insight?.id} />
        </div>
        <Observations o={data} />
      </div>}
    </div>
  );
}
